import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { describePasskeyError, isPasskeyCancelled } from '../lib/passkey-errors';

const TOKEN_QUERY_PARAM = 'token';
const DEFAULT_PASSKEY_NAME = 'Passkey';
const PASSKEY_NAME_MAX_LENGTH = 100;

export default function RegisterPasskeyPage() {
  // The token is read once and removed from the URL right away so it does not stay in
  // the address bar, browser history or referrers.
  const [token] = useState(() => {
    return new URLSearchParams(window.location.search).get(TOKEN_QUERY_PARAM) ?? '';
  });
  const [name, setName] = useState(DEFAULT_PASSKEY_NAME);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const { registerPasskeyWithToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has(TOKEN_QUERY_PARAM)) {
      url.searchParams.delete(TOKEN_QUERY_PARAM);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      await registerPasskeyWithToken(token, name.trim() || DEFAULT_PASSKEY_NAME);
      void navigate('/', { replace: true });
    } catch (err) {
      if (isPasskeyCancelled(err)) {
        setInfo('Registration was cancelled. Click the button to try again.');
      } else {
        setError(describePasskeyError(err, 'Passkey registration failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900'>
      <div className='w-full max-w-md rounded-lg bg-white p-8 shadow-md dark:bg-gray-800'>
        <h1 className='mb-6 text-center text-2xl font-bold text-gray-800 dark:text-white'>
          Register Passkey
        </h1>

        {!token ? (
          <div className='space-y-4'>
            <div className='rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900 dark:text-red-200'>
              The registration link is missing or invalid. Ask an administrator for a new one.
            </div>
            <Link
              to='/login'
              className='block text-center text-sm text-blue-600 hover:underline dark:text-blue-400'
            >
              Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={e => void handleSubmit(e)} className='space-y-4'>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              Register a passkey for this device to sign in to the admin panel. The registration
              link can be used only once.
            </p>

            <div>
              <label
                htmlFor='passkeyName'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300'
              >
                Passkey name
              </label>
              <input
                type='text'
                id='passkeyName'
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={PASSKEY_NAME_MAX_LENGTH}
                className='mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400'
                placeholder='e.g. MacBook, YubiKey'
              />
            </div>

            {error && (
              <div className='rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900 dark:text-red-200'>
                {error}
              </div>
            )}

            {info && (
              <div className='rounded-md bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                {info}
              </div>
            )}

            <button
              type='submit'
              disabled={loading}
              className='w-full rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:bg-gray-400 dark:bg-blue-500 dark:hover:bg-blue-600 dark:disabled:bg-gray-600'
            >
              {loading ? 'Waiting for passkey...' : 'Register passkey'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
